import React, { useState, useEffect } from "react"
import axios from "axios"
import { Link, useNavigate, useParams } from "react-router-dom"
import { FaAngleLeft } from 'react-icons/fa'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import { FaEdit } from 'react-icons/fa'
import { apiURL, sitePath } from 'config/globals'
import Topbar from 'components/Topbar'
import Breadcrumbs from 'components/Breadcrumbs'
import FormRow from "components/FormRow"
import CancelButton from 'components/CancelButton'
import SaveButton from 'components/SaveButton'
import Footer from 'components/Footer'
import { checkAuth, authForm, isSuperAdmin } from 'utils/Auth'

const UserEdit = () => {
    const [currentUser, setCurrentUser] = useState(false)
    const [museum, setMuseum] = useState({})
    const [dbRoles, setDbRoles] = useState([])
    const [dbUser, setDbUser] = useState({})
    const [form, setForm] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const navigate = useNavigate()
    const { entityId, userId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            await getDbRoles()
            await getDbUser(userId)
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getDbRoles = async () => {
        const response = await axios.get(`${apiURL}/get-db-roles/`)
        
        if (!response.data.success) {
            setErrorMessage(response.data.error || response.data)
            return
        }
        
        setDbRoles(response.data.rows)
    }

    const getDbUser = async (id) => {
        const response = await axios.get(`${apiURL}/get-db-user/${id}`)
        
        if (!response.data.success) {
            setErrorMessage(response.data.error || response.data)
            return
        }

        setDbUser(response.data.item)
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        setErrorMessage('')
        setSuccessMessage('')
        setIsSubmitting(true)

        try {
            const requestUrl = `${apiURL}/update-user/${dbUser.id}`
            const response = await axios.put(requestUrl, authForm(form))
            
            if (!response.data.success) {
                setErrorMessage(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            setIsSubmitting(false)
            setSuccessMessage("Data saved successfully")
        } catch (error) {
            setErrorMessage(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const navigateToChangePassword = () => {
        navigate(`/museum/${currentUser.entityId}/users/${currentUser.id}/change-password`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width" style={{paddingBottom: 80}}>
                <div>
                    <Link to={`/museum/${museum.id}/dashboard`} className="no-decoration">
                        <FaAngleLeft /> Back to Home
                    </Link>
                </div>
                <header className="mt-2">
                    <div className="float-start">
                        <h1>{dbUser.name}</h1>
                    </div>
                    {dbUser.id === currentUser.id &&
                        <Button variant="outline-primary" size="lg" className="float-end" onClick={navigateToChangePassword}>
                            <FaEdit /> Change Password
                        </Button>
                    }
                </header>
                {dbUser.id &&
                    <>
                        <FormRow
                            id="userRole"
                            label="Role"
                            content={
                                <div className="mt-2">
                                    {dbRoles.find(x => x.id === dbUser.role).name}
                                </div>
                            }
                        />
                        <FormRow
                            id="newUserName"
                            label="Name"
                            required={true}
                            content={
                                <Form.Control
                                    name="name"
                                    autoComplete="off"
                                    defaultValue={dbUser.name}
                                    disabled={dbUser.role === "1" || (dbUser.role === "2" && currentUser.role === "2" && dbUser.id !== currentUser.id)}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            id="newUserEmail"
                            label="Email"
                            required={true}
                            content={
                                <Form.Control
                                    name="email"
                                    autoComplete="off"
                                    defaultValue={dbUser.email}
                                    disabled={dbUser.role === "1" || (dbUser.role === "2" && currentUser.role === "2" && dbUser.id !== currentUser.id)}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        {errorMessage && 
                            <Alert key='submitError' variant='danger' className="mt-4">
                                {errorMessage}
                            </Alert>
                        }
                        {successMessage && 
                            <Alert key='submitSuccess' variant='success' className="mt-4">
                                {successMessage}
                            </Alert>
                        }
                        <div className="mt-5 mb-4 d-grid d-lg-block">
                            {((dbUser.role !== "1" && (currentUser.role !== "2" || (dbUser.role === "2" && dbUser.id === currentUser.id))) || parseInt(dbUser.role) > 2) &&
                                <SaveButton isSubmitting={isSubmitting} onClick={handleSubmit} title="Save" />
                            }
                        </div>
                    </>
                }
                {loadError &&
                    <Alert key='loadError' variant='danger'>
                        {loadError}
                    </Alert>
                }
                <Footer user={currentUser} />
            </main>
        </>
    )
}

export default UserEdit